import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { RefreshCw, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FyersStatusBanner = ({ onGoToSettings }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);

  const sessionToken = localStorage.getItem('admin_session_token');

  const load = useCallback(async () => {
    if (!sessionToken) { setLoading(false); return; }
    try {
      const r = await fetch(`${API_URL}/api/fyers-auth/status?session_token=${sessionToken}`);
      if (r.ok) setStatus(await r.json());
    } catch (_e) { /* ignore */ }
    finally { setLoading(false); }
  }, [sessionToken]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const reconnect = async () => {
    setReconnecting(true);
    try {
      const r = await fetch(`${API_URL}/api/fyers-auth/run-now?session_token=${sessionToken}`, { method: 'POST' });
      const j = await r.json();
      if (r.ok && j.status === 'ok') {
        toast.success('✅ Fyers reconnected — live data & F&O movers restored.');
      } else {
        const detail = j.detail || j.step || 'Reconnect failed';
        toast.error(`Fyers reconnect failed: ${detail}. Check credentials in Settings.`, { duration: 10000 });
      }
      await load();
    } catch (e) {
      toast.error(`Reconnect error: ${e.message}`);
    } finally {
      setReconnecting(false);
    }
  };

  if (loading || !status) return null;

  const valid = status.token_valid;
  const lastRun = status.last_run;

  if (valid) {
    return (
      <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm" data-testid="fyers-status-banner-ok">
        <CheckCircle2 className="w-4 h-4" />
        <span className="font-medium">Fyers live data feed: Connected</span>
        {status.token_updated_at && (
          <span className="text-green-600/70 text-xs ml-1">
            · token refreshed {new Date(status.token_updated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} IST
          </span>
        )}
        <Button variant="ghost" size="sm" onClick={load} className="ml-auto text-green-700 hover:bg-green-100 h-7" data-testid="fyers-refresh-status-btn">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border-2 border-red-300" data-testid="fyers-status-banner-error">
      <div className="flex items-start gap-3 flex-wrap">
        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-[240px]">
          <p className="text-red-800 font-semibold text-sm">Fyers live data feed is disconnected</p>
          <p className="text-red-700/80 text-xs mt-0.5">
            {status.credentials_set
              ? 'Your Fyers token has expired or the daily auto-login failed. F&O movers and live market data will show blank until you reconnect.'
              : 'Fyers credentials are not configured. Add them in Settings to enable live data.'}
            {lastRun && lastRun.status && lastRun.status !== 'ok' && (
              <span className="block mt-1">Last auto-auth: <strong>{lastRun.status}{lastRun.step ? ` @ ${lastRun.step}` : ''}</strong>{lastRun.detail ? ` — ${lastRun.detail}` : ''}</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {status.credentials_set ? (
            <Button onClick={reconnect} disabled={reconnecting} size="sm" className="bg-red-600 hover:bg-red-700 text-white" data-testid="fyers-reconnect-btn">
              {reconnecting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Reconnecting…</> : <><RefreshCw className="w-4 h-4 mr-1" /> Reconnect Fyers now</>}
            </Button>
          ) : (
            <Button onClick={onGoToSettings} size="sm" className="bg-red-600 hover:bg-red-700 text-white" data-testid="fyers-goto-settings-btn">
              Configure in Settings
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FyersStatusBanner;
